// /manufacturing-orders/:id — карточка заказа на производство.
//
// Общие поля, атрибуты и материалы по описанию заказа техпроцесса, номера
// изделий (диапазон по маске или внешние серийники), задания на стадию,
// короткий путь по стадиям маршрута, документы.
//
// Права: чтение — manufacturing_order.read; правка — manufacturing_order.create;
// запуск, приёмка партии, пакетная печать — manufacturing_order.launch;
// короткий путь — manufacturing_order.advance; удаление — manufacturing_order.delete.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { StatusPill } from "@/components/StatusPill";
import { hasModule } from "@/lib/auth";
import { useAuth } from "@/lib/AuthContext";
import { ApiError, manufacturingOrdersApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { localized } from "@/lib/localized";
import { useStageNames } from "@/lib/stages";
import type {
  ManufacturingOrderLaunchResponse,
  ManufacturingOrderPrintBatchResponse,
  ManufacturingOrderStatus,
  OrderAttributeDef,
  ShortFlowStage,
} from "@/lib/types";

export default function ManufacturingOrderDetail() {
  return (
    <RequirePermission codes="manufacturing_order.read">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission, user } = useAuth();
  const stageNames = useStageNames();
  const canEdit = hasPermission("manufacturing_order.create");
  const canLaunch = hasPermission("manufacturing_order.launch");
  const canDelete = hasPermission("manufacturing_order.delete");
  const shortFlowModule = hasModule(user, "oem-supply");
  const canAdvance = shortFlowModule && hasPermission("manufacturing_order.advance");
  const testReportModule = hasModule(user, "ro-test-report");
  const canTestReport = testReportModule && hasPermission("document.test_report.manage");
  const canDocCenter = hasPermission("document.print");

  const [launchResult, setLaunchResult] = useState<ManufacturingOrderLaunchResponse | null>(null);
  const [launchErr, setLaunchErr] = useState<string | null>(null);
  const [statusErr, setStatusErr] = useState<string | null>(null);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const [docErr, setDocErr] = useState<string | null>(null);

  const apiErr = (e: unknown, fallback: string) =>
    e instanceof ApiError ? `${e.code}: ${e.message}` : e instanceof Error ? e.message : fallback;

  const order = useQuery({
    queryKey: ["manufacturing-order", id],
    queryFn: () => manufacturingOrdersApi.get(id!),
    enabled: !!id,
    refetchInterval: 5000,
  });
  const processes = useQuery({
    queryKey: ["manufacturing-orders", "processes"],
    queryFn: () => manufacturingOrdersApi.processes(),
    staleTime: 60_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["manufacturing-order", id] });
    qc.invalidateQueries({ queryKey: ["manufacturing-orders"] });
  };

  const changeStatus = useMutation({
    mutationFn: (status: ManufacturingOrderStatus) => manufacturingOrdersApi.update(id!, { status }),
    onSuccess: invalidate,
    onError: (e) => setStatusErr(apiErr(e, t("mo.detail.statusFailed"))),
  });

  const launch = useMutation({
    mutationFn: () => manufacturingOrdersApi.launch(id!),
    onSuccess: (res) => {
      setLaunchResult(res);
      invalidate();
    },
    onError: (e) => setLaunchErr(apiErr(e, t("mo.detail.launchFailed"))),
  });

  const takeIntoWork = useMutation({
    mutationFn: () => manufacturingOrdersApi.takeIntoWork(id!),
    onSuccess: (res) => {
      setLaunchResult(res);
      invalidate();
      qc.invalidateQueries({ queryKey: ["mo-short-flow", id] });
    },
    onError: (e) => setLaunchErr(apiErr(e, t("mo.detail.launchFailed"))),
  });

  const del = useMutation({
    mutationFn: () => manufacturingOrdersApi.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["manufacturing-orders"] });
      navigate("/manufacturing-orders", { replace: true });
    },
    onError: (e) =>
      setDeleteErr(
        e instanceof ApiError && e.code === "CONFLICT" ? t("mo.detail.deleteConflict") : apiErr(e, t("mo.detail.deleteFailed")),
      ),
  });

  const receive = useMutation({
    mutationFn: () => manufacturingOrdersApi.receive(id!),
    onSuccess: () => {
      setDocErr(null);
      qc.invalidateQueries({ queryKey: ["manufacturing-order", id] });
    },
    onError: (e) => setDocErr(apiErr(e, t("mo.detail.receiveFailed"))),
  });

  const [printPassports, setPrintPassports] = useState(true);
  const [printCopies, setPrintCopies] = useState(1);
  const [printResult, setPrintResult] = useState<ManufacturingOrderPrintBatchResponse | null>(null);
  const printBatch = useMutation({
    mutationFn: () =>
      manufacturingOrdersApi.printBatch(id!, { passports: printPassports, packing_list: false, copies: printCopies }),
    onSuccess: (res) => {
      setDocErr(null);
      setPrintResult(res);
    },
    onError: (e) => setDocErr(apiErr(e, t("mo.detail.printFailed"))),
  });

  // Одна кнопка — все документы по заказу: при внешних серийниках приёмка
  // партии (если нужно) и пакет паспортов; протокол и упаковочный лист
  // открываются заполненными в новых вкладках.
  const [allDocsMsg, setAllDocsMsg] = useState<string | null>(null);
  const printAll = useMutation({
    mutationFn: async () => {
      const ord = order.data;
      const cfgNow = processes.data?.find((p) => p.key === ord?.process_key)?.order;
      const external = cfgNow?.serials.mode === "external";
      const launchedNow = ord?.status === "IN_PRODUCTION" || ord?.status === "COMPLETED";
      const errs: string[] = [];
      let passports = 0;
      if (external && launchedNow) {
        const serials = ord?.external_serials ?? [];
        if (!serials.every((s) => s.item_id != null) && ord?.status === "IN_PRODUCTION") {
          try {
            await manufacturingOrdersApi.receive(id!);
          } catch (e) {
            errs.push(apiErr(e, t("mo.detail.receiveFailed")));
          }
        }
        try {
          const pb = await manufacturingOrdersApi.printBatch(id!, { passports: true, packing_list: false, copies: 1 });
          passports = pb.passport_jobs.length;
          pb.errors.forEach((er) => errs.push(`${er.item_id}: ${er.error}`));
        } catch (e) {
          errs.push(apiErr(e, t("mo.detail.printFailed")));
        }
      }
      return { external: external && launchedNow, passports, errs };
    },
    onSuccess: (r) => {
      setDocErr(null);
      qc.invalidateQueries({ queryKey: ["manufacturing-order", id] });
      if (testReportModule) {
        window.open(`/admin/print/test-reports?manufacturing_order_id=${id}`, "_blank", "noopener");
      }
      window.open(`/admin/print/packing-list?manufacturing_order_id=${id}`, "_blank", "noopener");
      const parts: string[] = [];
      if (r.external) parts.push(t("mo.detail.passportPackage", { count: r.passports }));
      parts.push(t("mo.detail.tabsOpened"));
      setAllDocsMsg(parts.join("; ") + (r.errs.length ? ` · ${r.errs.join("; ")}` : ""));
    },
    onError: (e) => setDocErr(apiErr(e, t("mo.detail.printFailed"))),
  });

  if (!id) return <ErrorState error={new Error("Bad order id")} />;
  if (order.isLoading) return <LoadingState />;
  if (order.isError) return <ErrorState error={order.error} />;

  const o = order.data!;
  const process = processes.data?.find((p) => p.key === o.process_key);
  const cfg = process?.order;
  const serialMode = cfg?.serials.mode ?? "none";
  const tasks = o.production_orders ?? [];
  const externalSerials = o.external_serials ?? [];
  const receivedCount = externalSerials.filter((s) => s.item_id != null).length;
  const allReceived = externalSerials.length > 0 && receivedCount === externalSerials.length;
  const launched = o.status === "IN_PRODUCTION" || o.status === "COMPLETED";
  const showDocs = (canLaunch || canTestReport) && o.status !== "CANCELLED";
  const showBatch = serialMode === "external" && canLaunch && launched;
  const showTestReport = canTestReport && o.status !== "CANCELLED";

  const attrValue = (d: OrderAttributeDef): string | number | null => {
    const v = o.attributes[d.key];
    if (v == null || v === "") return null;
    if (d.type === "boolean") return v === true ? t("common.yes") : t("common.no");
    return typeof v === "number" || typeof v === "string" ? v : String(v);
  };

  return (
    <>
      <PageHeader
        eyebrow={`${t("mo.eyebrow")} · ${o.code}`}
        title={o.article ?? o.code}
        description={process ? process.name : (o.process_key ?? "")}
        actions={
          <div className="flex items-center gap-2">
            {canEdit && o.status !== "COMPLETED" && o.status !== "CANCELLED" && (
              <button className="btn" onClick={() => navigate(`/manufacturing-orders/${o.id}/edit`)}>
                {t("common.edit")}
              </button>
            )}
            {canEdit && o.status === "DRAFT" && (
              <button
                className="btn"
                onClick={() => {
                  setStatusErr(null);
                  changeStatus.mutate("READY");
                }}
                disabled={changeStatus.isPending}
              >
                {changeStatus.isPending ? "…" : t("mo.detail.markReady")}
              </button>
            )}
            {canEdit && o.status === "READY" && (
              <button
                className="btn"
                onClick={() => {
                  setStatusErr(null);
                  changeStatus.mutate("DRAFT");
                }}
                disabled={changeStatus.isPending}
              >
                {t("mo.detail.backToDraft")}
              </button>
            )}
            {canAdvance && (o.status === "DRAFT" || o.status === "READY") && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  setLaunchErr(null);
                  takeIntoWork.mutate();
                }}
                disabled={takeIntoWork.isPending}
                title={t("mo.detail.takeIntoWorkHint")}
              >
                {takeIntoWork.isPending ? "…" : t("mo.detail.takeIntoWork")}
              </button>
            )}
            {canLaunch && o.status === "READY" && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  setLaunchErr(null);
                  launch.mutate();
                }}
                disabled={launch.isPending}
              >
                {launch.isPending ? "…" : t("mo.detail.launch")}
              </button>
            )}
            {canDelete && (
              <button
                className="btn btn-danger"
                onClick={() => {
                  setDeleteErr(null);
                  if (window.confirm(t("mo.detail.deleteConfirm", { code: o.code }))) del.mutate();
                }}
                disabled={del.isPending}
              >
                {del.isPending ? "…" : t("common.delete")}
              </button>
            )}
          </div>
        }
      />

      {deleteErr && <ErrorBanner onDismiss={() => setDeleteErr(null)}>{deleteErr}</ErrorBanner>}
      {statusErr && <ErrorBanner onDismiss={() => setStatusErr(null)}>{statusErr}</ErrorBanner>}
      {launchErr && <ErrorBanner onDismiss={() => setLaunchErr(null)}>{launchErr}</ErrorBanner>}

      {launchResult && (
        <section className="card p-5 border-accent/40 space-y-3">
          <h2 className="text-lg">{t("mo.detail.launched")}</h2>
          <p className="text-sm text-text-secondary">
            {t("mo.detail.createdTasks", { count: launchResult.created_production_orders.length })}
          </p>
          {launchResult.created_production_orders.length > 0 && (
            <ul className="text-sm space-y-1">
              {launchResult.created_production_orders.map((task) => (
                <li key={task.id}>
                  <Link to={`/orders/${task.id}`} className="font-mono text-accent-strong hover:underline">
                    {task.code}
                  </Link>{" "}
                  <span className="text-text-muted">
                    · {stageNames.byId(task.stage_id)} · {t("mo.detail.plan")} {task.target_quantity}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {launchResult.skipped_stage_codes.length > 0 && (
            <p className="text-xs text-text-muted">
              {t("mo.detail.skippedStages")}{" "}
              <span className="font-mono">{launchResult.skipped_stage_codes.join(", ")}</span>
            </p>
          )}
        </section>
      )}

      {launched && shortFlowModule && (
        <ShortFlowChecklist orderId={id} canAdvance={canAdvance} stageName={stageNames.byCode} />
      )}

      <section className="card p-5">
        <div className="flex items-center justify-between gap-4 mb-4">
          <h2 className="text-lg">{t("mo.detail.parameters")}</h2>
          <div className="flex items-center gap-2">
            <StatusPill kind="manufacturing" value={o.status} />
            <span className="text-xs text-text-muted">{t(`mo.status.${o.status}`, o.status)}</span>
            <span className="pill">{o.source_system === "staiter" ? t("mo.source.external") : t("mo.source.manual")}</span>
          </div>
        </div>
        <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
          <Row label={t("mo.fields.article")} value={o.article} mono />
          <Row label={t("mo.fields.customerCode")} value={o.customer_code} mono />
          <Row label={t("mo.fields.orderDate")} value={o.order_date} />
          <Row label={t("mo.fields.elementCount")} value={o.element_count} />
          {(cfg?.attributes ?? []).map((d) => (
            <Row
              key={d.key}
              label={`${localized(d.label, d.key)}${d.unit ? `, ${d.unit}` : ""}`}
              value={attrValue(d)}
            />
          ))}
          {(cfg?.materials ?? []).map((d) => {
            const m = o.materials.find((x) => x.slot === d.slot);
            const text = m?.material_id
              ? `${m.material_code ?? ""}${m.material_name ? ` · ${m.material_name}` : ""}${m.batch_no ? ` · ${t("mo.form.batch")} ${m.batch_no}` : ""}`
              : null;
            return <Row key={d.slot} label={localized(d.label, d.slot)} value={text} />;
          })}
        </dl>
      </section>

      {serialMode === "external" && (
        <section className="card p-5 space-y-2">
          <h2 className="text-lg">{t("mo.form.externalSerials")}</h2>
          {externalSerials.length > 0 ? (
            <>
              <p className="text-sm text-text-muted">
                {t("mo.form.externalSerialsCount")}{" "}
                <span className="font-mono tabular-nums">{externalSerials.length}</span>
              </p>
              <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1 text-sm">
                {externalSerials.map((s) => (
                  <li key={s.id} className="font-mono tabular-nums">
                    <span className="text-text-muted">{s.position}.</span> {s.external_serial}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm text-text-muted">{t("mo.detail.noExternalSerials")}</p>
          )}
        </section>
      )}

      {serialMode === "mask" && (
        <section className="card p-5 space-y-2">
          <h2 className="text-lg">{t("mo.detail.serials")}</h2>
          {o.serial_from != null && o.serial_to != null ? (
            <div className="flex flex-wrap gap-8">
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("mo.detail.serialRange")}</p>
                <p className="text-xl font-mono tabular-nums mt-1">
                  {o.serial_from} … {o.serial_to}
                </p>
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("mo.detail.serialExample")}</p>
                <p className="text-xl font-mono mt-1">{o.serial_example ?? "—"}</p>
                {cfg?.serials.mask && (
                  <p className="text-xs text-text-muted mt-1">
                    {t("mo.form.serialMask")} <span className="font-mono">{cfg.serials.mask}</span>
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-text-muted">{t("mo.detail.serialsNotReserved")}</p>
          )}
        </section>
      )}

      {showDocs && (
        <section className="card p-5 space-y-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg">{t("mo.detail.documents")}</h2>
              <p className="text-sm text-text-muted mt-1">
                {serialMode === "external" ? t("mo.detail.documentsExternal") : t("mo.detail.documentsMask")}
              </p>
            </div>
            {canDocCenter && (
              <Link to={`/admin/documents?order=${o.id}`} className="btn btn-sm shrink-0">
                {t("mo.detail.allDocuments")}
              </Link>
            )}
          </div>

          {docErr && <ErrorBanner onDismiss={() => setDocErr(null)}>{docErr}</ErrorBanner>}

          {o.status !== "DRAFT" && (
            <div className="rounded-md border border-accent/40 bg-surface-alt p-4 space-y-2">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setAllDocsMsg(null);
                  setPrintResult(null);
                  printAll.mutate();
                }}
                disabled={printAll.isPending}
              >
                {printAll.isPending ? "…" : t("mo.detail.printAll")}
              </button>
              <p className="text-xs text-text-muted">
                {serialMode === "external" ? t("mo.detail.printAllExternal") : t("mo.detail.printAllMask")}
              </p>
              {allDocsMsg && <p className="text-sm rounded-md bg-surface p-2">{allDocsMsg}</p>}
            </div>
          )}
          {o.status === "DRAFT" && (
            <div className="rounded-md bg-surface-alt p-3 text-sm text-text-secondary">{t("mo.detail.draftDocsHint")}</div>
          )}

          {showBatch && (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm">
                {t("mo.detail.received")}{" "}
                <span className="font-mono tabular-nums">
                  {receivedCount} / {externalSerials.length}
                </span>
              </span>
              {!allReceived && o.status === "IN_PRODUCTION" && (
                <button
                  className="btn"
                  onClick={() => {
                    setDocErr(null);
                    receive.mutate();
                  }}
                  disabled={receive.isPending || externalSerials.length === 0}
                >
                  {receive.isPending ? "…" : t("mo.detail.receive")}
                </button>
              )}
              {allReceived && <span className="pill text-xs">{t("mo.detail.batchReceived")}</span>}
            </div>
          )}

          {showBatch && (
            <div className="space-y-3 border-t border-border pt-4">
              <div className="flex flex-wrap items-center gap-5">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={printPassports} onChange={(e) => setPrintPassports(e.target.checked)} />
                  {t("mo.detail.passportsPerItem")}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  {t("mo.detail.copies")}
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={printCopies}
                    onChange={(e) => setPrintCopies(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                    className="w-16 px-2 py-1 rounded-md bg-surface border border-border text-sm font-mono"
                  />
                </label>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setDocErr(null);
                  setPrintResult(null);
                  printBatch.mutate();
                }}
                disabled={printBatch.isPending || !printPassports || receivedCount === 0}
              >
                {printBatch.isPending ? "…" : t("mo.detail.printPassports")}
              </button>
              {printPassports && receivedCount === 0 && (
                <p className="text-xs text-warn">{t("mo.detail.receiveFirst")}</p>
              )}
              {printResult && (
                <div className="text-sm space-y-1 rounded-md bg-surface-alt p-3">
                  <p>
                    {printResult.passport_package_job_id
                      ? t("mo.detail.passportPackage", { count: printResult.passport_jobs.length })
                      : t("mo.detail.passportsNone")}
                  </p>
                  {printResult.errors.length > 0 && (
                    <ul className="list-disc ml-5 text-danger">
                      {printResult.errors.map((er) => (
                        <li key={er.item_id} className="font-mono text-xs">
                          {er.item_id}: {er.error}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {showTestReport && (
            <div className="border-t border-border pt-4 flex flex-wrap gap-2">
              <button className="btn" onClick={() => navigate(`/admin/print/test-reports?manufacturing_order_id=${o.id}`)}>
                {t("mo.detail.testReport")}
              </button>
              <button className="btn" onClick={() => navigate(`/admin/print/packing-list?manufacturing_order_id=${o.id}`)}>
                {t("mo.detail.packingList")}
              </button>
            </div>
          )}
        </section>
      )}

      <section className="card overflow-hidden">
        <div className="p-5 pb-3">
          <h2 className="text-lg">{t("mo.detail.tasks")}</h2>
          <p className="text-sm text-text-muted mt-1">{t("mo.detail.tasksHint")}</p>
        </div>
        {tasks.length === 0 ? (
          <p className="px-5 pb-5 text-sm text-text-muted">{t("mo.detail.noTasks")}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-4 py-3">{t("mo.detail.colCode")}</th>
                <th className="px-4 py-3">{t("mo.detail.colStage")}</th>
                <th className="px-4 py-3 text-right">{t("mo.detail.plan")}</th>
                <th className="px-4 py-3 text-right">{t("mo.detail.colDone")}</th>
                <th className="px-4 py-3 text-right">{t("mo.detail.colRejected")}</th>
                <th className="px-4 py-3">{t("mo.detail.colStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id} className="border-t border-border hover:bg-surface-hover">
                  <td className="px-4 py-3">
                    <Link to={`/orders/${task.id}`} className="font-mono text-accent-strong hover:underline">
                      {task.code}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-text-muted">{stageNames.byId(task.stage_id)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{task.target_quantity}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{task.completed_quantity}</td>
                  <td
                    className={`px-4 py-3 text-right tabular-nums ${task.rejected_quantity > 0 ? "text-danger" : "text-text-muted"}`}
                  >
                    {task.rejected_quantity}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill kind="order" value={task.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {o.comment && (
        <section className="card p-5">
          <h2 className="text-lg mb-2">{t("mo.fields.comment")}</h2>
          <p className="text-sm text-text-secondary whitespace-pre-wrap">{o.comment}</p>
        </section>
      )}

      <p className="text-xs text-text-muted">
        {t("mo.detail.created")} {formatDateTime(o.created_at)} · {t("mo.detail.updated")} {formatDateTime(o.updated_at)}
        {o.launched_at ? ` · ${t("mo.detail.launchedAt")} ${formatDateTime(o.launched_at)}` : ""}
      </p>
    </>
  );
}

// Короткий путь: стадии маршрута отмечаются «готово» по порядку.  Сервер
// следит за порядком; здесь кнопка активна только у первой незавершённой.
function ShortFlowChecklist({
  orderId,
  canAdvance,
  stageName,
}: {
  orderId: string;
  canAdvance: boolean;
  stageName: (code: string) => string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["mo-short-flow", orderId],
    queryFn: () => manufacturingOrdersApi.stages(orderId),
    refetchInterval: 5000,
  });
  const advance = useMutation({
    mutationFn: (stageCode: string) => manufacturingOrdersApi.advanceStage(orderId, stageCode),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ["mo-short-flow", orderId] });
      qc.invalidateQueries({ queryKey: ["manufacturing-order", orderId] });
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? `${e.code}: ${e.message}` : e instanceof Error ? e.message : t("mo.detail.advanceFailed")),
  });

  const stages: ShortFlowStage[] = q.data?.stages ?? [];
  if (q.isLoading || stages.length === 0) return null;
  const nextIdx = stages.findIndex((s) => s.status !== "COMPLETED" && s.status !== "CANCELLED");
  const stateLabel = (s: ShortFlowStage): string => {
    if (s.status === "COMPLETED") return t("mo.shortFlow.done");
    if (s.status === "CANCELLED") return t("mo.shortFlow.cancelled");
    if (s.status === "ACTIVE" || s.status === "ADJUSTED") return t("mo.shortFlow.inWork");
    return t("mo.shortFlow.waiting");
  };

  return (
    <section className="card p-5 space-y-3">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg">{t("mo.shortFlow.title")}</h2>
        <span className="text-xs text-text-muted">{t("mo.shortFlow.hint")}</span>
      </div>
      {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
      <ol className="space-y-2">
        {stages.map((s, i) => {
          const done = s.status === "COMPLETED";
          const cancelled = s.status === "CANCELLED";
          const isNext = i === nextIdx;
          return (
            <li key={s.production_order_id} className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-mono ${
                  done
                    ? "bg-accent/20 text-accent-strong"
                    : cancelled
                      ? "bg-surface-hover text-text-muted line-through"
                      : "bg-surface-hover text-text-secondary"
                }`}
              >
                {done ? "✓" : i + 1}
              </span>
              <span className="flex-1 text-sm">
                {stageName(s.stage_code)}
                <span className="ml-2 font-mono text-xs text-text-muted">{s.stage_code}</span>
              </span>
              <span className="text-xs text-text-muted">{stateLabel(s)}</span>
              {canAdvance && !done && !cancelled && (
                <button
                  className="btn text-xs px-2 py-1"
                  disabled={!isNext || advance.isPending}
                  onClick={() => {
                    setErr(null);
                    advance.mutate(s.stage_code);
                  }}
                >
                  {advance.isPending && isNext ? "…" : t("mo.shortFlow.done")}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function Row({ label, value, mono }: { label: string; value: string | number | null | undefined; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</dt>
      <dd className={`mt-0.5 ${mono ? "font-mono" : ""}`}>{value != null && value !== "" ? value : "—"}</dd>
    </div>
  );
}
